import { useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { nanoid } from "nanoid";
import { FormGroup, Input, Select } from "@cofano/cck";
import { Alert, Grid2 as Grid } from "@mui/material";
import styled from "styled-components";
import { FormDialog } from "components/dialogs/Dialogs";
import WidgetConfigSection from "view/dashboard/components/WidgetConfigSection";
import WidgetGrid from "view/dashboard/components/WidgetGrid";
import { WidgetTemplate } from "view/dashboard/types/widget";
import { compatibleVisuals } from "view/dashboard/utils/DashboardUtils";
import { RegisteredSource, SOURCES, SourceId } from "view/dashboard/widgets/sources";
import { VISUALS, VisualId } from "view/dashboard/widgets/visuals";
import { intl } from "LocaleWrapper";

const FORM_ID = "widgetBuilderForm";

const Placeholder = styled.div`
	align-items: center;
	display: flex;
	font-size: 13px;
	height: 250px;
	justify-content: center;
	padding: 16px;
	text-align: center;
`;

type Option<Id,> = {
	id: Id;
	label: string;
};

type BuilderValues = {
	source: SourceId | null;
	visual: VisualId | null;
	config: {
		label: string;
		url: string;
		source: Record<string, unknown>;
		visual: Record<string, unknown>;
	};
};

const EMPTY: BuilderValues = {
	source: null,
	visual: null,
	config: { label: "", url: "", source: {}, visual: {} },
};

const toValues = (template?: WidgetTemplate): BuilderValues => template
	? {
		source: template.source,
		visual: template.visual,
		config: {
			label: template.config.label,
			url: template.config.url,
			source: template.config.source as Record<string, unknown>,
			visual: template.config.visual as Record<string, unknown>,
		},
	}
	: EMPTY;

const sourceOptions: Array<Option<SourceId>> =
	(Object.entries(SOURCES) as Array<[ SourceId, RegisteredSource ]>)
		.map(([ id, source ]) => ({ id, label: source.label }));

type WidgetBuilderDialogProps = {
	open: boolean;
	onClose: () => void;
	template?: WidgetTemplate;
	order?: number;
	onSave: (template: WidgetTemplate) => void;
};

const WidgetBuilderDialog = ({ open, onClose, template, order = 0, onSave }: WidgetBuilderDialogProps) => {
	const form = useForm<BuilderValues>({ defaultValues: toValues(template) });
	const { control, handleSubmit, watch, setValue, setError, formState, reset } = form;

	const sourceId = watch("source");
	const visualId = watch("visual");

	const source = sourceId ? SOURCES[sourceId] : undefined;
	const visual = visualId ? VISUALS[visualId] : undefined;

	// Only visuals that can draw what this source produces
	const visualOptions: Array<Option<VisualId>> = useMemo(
		() => source
			? compatibleVisuals(source, VISUALS).map(([ id, candidate ]) => ({ id, label: candidate.label }))
			: [],
		[ source ],
	);

	useEffect(() => {
		if (open) {
			reset(toValues(template));
		}
	}, [ open ]);

	// auto select visual if there's only one available
	useEffect(() => {
		if (visualId && visualOptions.some(option => option.id === visualId)) {
			return;
		}

		setValue("visual", visualOptions.length === 1 ? visualOptions[0].id : null);
		setValue("config.visual", {});
	}, [ visualOptions ]);

	const values = useWatch({ control }) as BuilderValues;

	const buildPreview = (): WidgetTemplate | undefined => {
		if (!sourceId || !visualId || !source || !visual) {
			return undefined;
		}

		const sourceConfig = source.configSchema.safeParse(values.config?.source ?? {});
		const visualConfig = visual.configSchema.safeParse(values.config?.visual ?? {});
		if (!sourceConfig.success || !visualConfig.success) {
			return undefined;
		}

		return {
			id: "preview",
			source: sourceId,
			visual: visualId,
			config: {
				label: values.config?.label ?? "",
				url: values.config?.url ?? "",
				source: sourceConfig.data,
				visual: visualConfig.data,
			},
			order: 0,
		};
	};

	const preview = buildPreview();

	const save = (values: BuilderValues) => {
		if (!values.source || !values.visual || !source || !visual) {
			return;
		}

		const sourceConfig = source.configSchema.safeParse(values.config.source);
		if (!sourceConfig.success) {
			setError("config.source", { message: sourceConfig.error.issues[0]?.message });
			return;
		}

		const visualConfig = visual.configSchema.safeParse(values.config.visual);
		if (!visualConfig.success) {
			setError("config.visual", { message: visualConfig.error.issues[0]?.message });
			return;
		}

		onSave({
			id: template?.id ?? nanoid(),
			source: values.source,
			visual: values.visual,
			config: {
				label: values.config.label,
				url: values.config.url,
				source: sourceConfig.data,
				visual: visualConfig.data,
			},
			order: template?.order ?? order,
		});

		onClose();
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: template ? "WIDGET_EDIT" : "WIDGET_ADD" })}
			form={FORM_ID}
			maxWidth="lg"
			saveButtonDisabled={!source || !visual}>
			<FormProvider {...form}>
				<form id={FORM_ID} onSubmit={event => void handleSubmit(save)(event)}>
					<Grid spacing={4} container>
						<Grid size={{ xs: 12, md: 9 }}>
							<Grid spacing={2} container>
								<Grid size={{ xs: 12, sm: 6 }}>
									<FormGroup label={intl.formatMessage({ id: "LABEL" })} overflow="hidden" required>
										<Controller
											name="config.label"
											control={control}
											rules={{ required: true }}
											render={({ field }) => (
												<Input {...field} />
											)} />
									</FormGroup>
								</Grid>

								<Grid size={{ xs: 12, sm: 6 }}>
									<FormGroup label={intl.formatMessage({ id: "NAVIGATES_TO" })} overflow="hidden">
										<Controller
											name="config.url"
											control={control}
											render={({ field }) => (
												<Input {...field} />
											)} />
									</FormGroup>
								</Grid>

								{/* Source */}
								<Grid size={{ xs: 12, sm: 6 }}>
									<FormGroup label={intl.formatMessage({ id: "WIDGET_SOURCE" })} overflow="hidden" required>
										<Controller
											name="source"
											control={control}
											render={({ field }) => (
												<Select<Option<SourceId>, false>
													fetchOptions={sourceOptions}
													getOptionLabel={option => option.label}
													value={sourceOptions.find(option => option.id === field.value) ?? null}
													onChange={option => {
														field.onChange(option?.id ?? null);
														setValue("config.source", {});
													}} />
											)} />
									</FormGroup>

									{source && <WidgetConfigSection
										title={source.label}
										prefix="config.source"
										form={source.form} />}
								</Grid>

								{/* Visual */}
								<Grid size={{ xs: 12, sm: 6 }}>
									<FormGroup label={intl.formatMessage({ id: "WIDGET_VISUAL" })} overflow="hidden" required>
										<Controller
											name="visual"
											control={control}
											render={({ field }) => (
												<Select<Option<VisualId>, false>
													disabled={!source}
													fetchOptions={visualOptions}
													getOptionLabel={option => option.label}
													value={visualOptions.find(option => option.id === field.value) ?? null}
													onChange={option => {
														field.onChange(option?.id ?? null);
														setValue("config.visual", {});
													}} />
											)} />
									</FormGroup>

									{visual && <WidgetConfigSection
										title={visual.label}
										prefix="config.visual"
										form={visual.form} />}
								</Grid>
							</Grid>
						</Grid>

						<Grid size={{ xs: 12, md: 3 }}>
							<FormGroup label={intl.formatMessage({ id: "PREVIEW" })} overflow="hidden">
								<div style={{ maxWidth: 280, pointerEvents: "none", border: "1px dashed gray", borderRadius: 4, overflow: "hidden" }}>
									{preview
										? <WidgetGrid widgets={[ preview ]} />
										: <Placeholder>{intl.formatMessage({ id: "WIDGET_PICK_SOURCE_AND_VISUAL" })}</Placeholder>}
								</div>
							</FormGroup>
						</Grid>
					</Grid>

					{formState.errors.config?.source?.message && (
						<Alert severity="error">{formState.errors.config.source.message.message}</Alert>
					)}

					{formState.errors.config?.visual?.message && (
						<Alert severity="error">{formState.errors.config.visual.message.message}</Alert>
					)}
				</form>
			</FormProvider>
		</FormDialog>
	);
};

export default WidgetBuilderDialog;
